import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArchiveDialog } from './ArchiveDialog';
import styles from './Recovery.module.css';
import { TrashDialog } from './TrashDialog';

export type RecoveryKind = 'archive' | 'trash';
type Open = RecoveryKind | null;

interface Props {
  boardId: string;
  /** The viewer may delete tasks (offered in the archive; the server decides). */
  canDeleteTasks?: boolean;
  /** Controlled mode: lets another control (the phone board menu) open the dialogs. */
  open?: Open;
  onOpenChange?: (open: Open) => void;
  /** Render the floating icons. Off on phones, where the board menu opens the dialogs. */
  showButtons?: boolean;
  /** Toggle of the board's hidden system Calendar column (shown only in the Kanban view). */
  calendarColumn?: { visible: boolean; count: number; onToggle: () => void };
}

function CalendarColumnIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6h16v14H4zM4 10h16M8 3v4M16 3v4"
      />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        d="M3 4h18v4H3zM5 8v12h14V8M10 12h4"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6"
      />
    </svg>
  );
}

/** Compact fixed Archive / Trash buttons in the bottom-right corner of the board. */
export function RecoveryDock({
  boardId,
  canDeleteTasks = false,
  open: controlled,
  onOpenChange,
  showButtons = true,
  calendarColumn,
}: Props) {
  const { t } = useTranslation();
  const [inner, setInner] = useState<Open>(null);
  const open = controlled !== undefined ? controlled : inner;
  const setOpen = (next: Open) => {
    setInner(next);
    onOpenChange?.(next);
  };
  const close = () => {
    setOpen(null);
  };
  return (
    <>
      {showButtons && (
        <div className={styles.dock}>
          {calendarColumn && (
            <button
              type="button"
              className={`${styles.dockButton} ${calendarColumn.visible ? styles.dockActive : ''}`}
              aria-label={t(calendarColumn.visible ? 'calendarColumn.hide' : 'calendarColumn.show')}
              title={t(calendarColumn.visible ? 'calendarColumn.hide' : 'calendarColumn.show')}
              aria-pressed={calendarColumn.visible}
              data-testid="calendar-column-toggle"
              onClick={calendarColumn.onToggle}
            >
              <CalendarColumnIcon />
              {calendarColumn.count > 0 && (
                <span className={styles.dockBadge} aria-hidden>
                  {calendarColumn.count > 99 ? '99+' : calendarColumn.count}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            className={styles.dockButton}
            aria-label={t('archive.open')}
            title={t('archive.open')}
            onClick={() => {
              setOpen('archive');
            }}
          >
            <ArchiveIcon />
          </button>
          <button
            type="button"
            className={styles.dockButton}
            aria-label={t('trash.open')}
            title={t('trash.open')}
            onClick={() => {
              setOpen('trash');
            }}
          >
            <TrashIcon />
          </button>
        </div>
      )}
      {open === 'archive' && (
        <ArchiveDialog boardId={boardId} canDelete={canDeleteTasks} onClose={close} />
      )}
      {open === 'trash' && <TrashDialog boardId={boardId} onClose={close} />}
    </>
  );
}
